/**
 * useTokenGate Hook
 *
 * Consumes TokenGateContext — the app-wide owner of the ONE insufficient-tokens
 * purchase surface (`components/tokens/InsufficientTokensModal`). Mounted in
 * App.tsx by `TokenGateProvider`.
 *
 * Use this instead of building a per-feature "buy tokens" affordance: there is
 * exactly one modal, it already lists the packages and starts the checkout, and
 * a second one would drift from it (RULE #-2).
 *
 * Shape mirrors useTokenBalance.ts: the context lives in contexts/, the hook
 * lives here, so the provider file exports only its component
 * (react-refresh/only-export-components).
 */

import { useContext } from 'react';
import { TokenGateContext } from '../contexts/TokenGateContext';

interface UseTokenGateReturn {
  /** Run `onSuccess` if the balance covers `required`, else open the modal. */
  checkTokens: (required: number, operationName: string, onSuccess: () => void) => void;
  /** Promise form of checkTokens; resolves false if the user closes the modal. */
  requireTokens: (required: number, operationName: string) => Promise<boolean>;
  /**
   * Open the modal because the SERVER refused for lack of tokens (402).
   * Pass the quote the surface displayed, or `null` when it displays none.
   */
  reportInsufficientTokens: (required: number | null, operationName: string) => void;
}

/**
 * Access the app-wide token gate.
 *
 * @returns The gate API (pre-flight checks + server-refusal reporting).
 * @throws If used outside TokenGateProvider.
 */
export function useTokenGate(): UseTokenGateReturn {
  const context = useContext(TokenGateContext);

  if (!context) {
    throw new Error('useTokenGate must be used within a TokenGateProvider');
  }

  return context;
}

export default useTokenGate;
